import React from 'react';

// The server's defaults for this instrument. windowMs must be a NUMBER: the
// runner derives its reaction-time ceiling as `timing.windowMs + 250`, so a
// string window would concatenate ("2500" + 250) and every response would be
// discarded as out of range.
const DEFAULT_WINDOW_MS = 2500;
const DEFAULT_ISI_MS = 1500;
const FIXATION_MS = 400;

// These are stimulus data, not theme tokens: the task is to name the ink
// colour, so the colours must be literal and identical in dark and light mode.
// They are deliberately exempt from the CSS-variable rule.
// Every value is picked to clear 3:1 against BOTH --bg-primary values
// (#1A1917 dark, #F7F6F3 light) — do not "restore" them to pure hues.
const INK = {
  RED: '#E5484D',
  // Dual-theme legible green: pure #30A46C is only 2.82:1 on the light bg.
  GREEN: '#238F5C',
  BLUE: '#3E63DD',
  // Dual-theme legible yellow: pure #F5D90A is 1.27:1 on the light bg.
  YELLOW: '#A07C00',
};

// Constant for the entire run, and deliberately not alphabetical. A shuffling
// button order turns every trial into a visual search for the button instead of
// a colour-naming judgement. There is no targetsFor here for the same reason:
// the runner would call it per trial.
const COLORS = ['RED', 'GREEN', 'BLUE', 'YELLOW'];

function StimulusView({ stimulus, compact }) {
  return (
    <span
      // The word alone would announce the WRONG answer to a screen reader —
      // naming the ink is the task. Both facts are already in the served
      // stimulus, so the label reveals nothing the client was not given.
      role="img"
      aria-label={`the word ${stimulus.word} printed in ${stimulus.inkColor}`}
      style={{
        fontSize: compact ? '28px' : 'clamp(48px, 13vw, 104px)',
        fontWeight: 800,
        letterSpacing: '-0.02em',
        // An unrecognised ink name must still render legible text rather than
        // inherit an arbitrary colour from the runner's chrome.
        color: INK[stimulus.inkColor] || 'var(--text-primary)',
      }}
    >
      {stimulus.word}
    </span>
  );
}

export const stroopTask = {
  id: 'stroop',
  name: 'Color Conflict',
  pillar: 'attention',

  // Forced choice: every trial ends in a response, so trial length carries no
  // information about what the participant did and there is no rhythm to leak.
  // The trial may end as soon as an answer lands.
  holdsFullWindow: false,

  instructions: [
    'A colour word will appear, printed in a colour.',
    'Name the INK COLOUR it is printed in.',
    'Ignore what the word says.',
  ],
  targets: COLORS.map((c, i) => ({
    id: c,
    label: c.charAt(0) + c.slice(1).toLowerCase(),
    keys: [String(i + 1)],
    hint: String(i + 1),
    color: INK[c],
  })),

  examples: [
    {
      stimulus: { word: 'RED', inkColor: 'BLUE' },
      response: 'Blue',
      note: 'printed in blue',
    },
    {
      stimulus: { word: 'GREEN', inkColor: 'GREEN' },
      response: 'Green',
    },
  ],

  // The server compares against its own uppercase ink name, so the target ids
  // ARE those names. Lowercasing or sending the hex here grades every trial
  // wrong while still looking like a plausible payload.
  toAnswer: (targetId) => ({ color: targetId }),

  // Deliberately ignores the per-trial second argument. The score is the
  // congruency contrast and the client is never told which trials are which;
  // pacing that varied with the stimulus would both leak the classification
  // and change what the contrast measures.
  timing: (params) => {
    const isiMs = Number(params?.isi_ms) || DEFAULT_ISI_MS;
    return {
      fixationMs: FIXATION_MS,
      windowMs: Number(params?.response_window_ms) || DEFAULT_WINDOW_MS,
      // The fixation cross is drawn inside the served inter-stimulus interval,
      // not on top of it, so it comes out of the gap: fixation + gap === isi_ms.
      //
      // Still this module's arithmetic, but no longer the whole of the REALIZED
      // interval — this task does not hold its full window, so an answered
      // trial ends 250ms after the response and its offset-to-next-onset is
      // `isi_ms + 250`, while an unanswered one still ends on the window. The
      // post-response interval is constant rather than reaction-time dependent,
      // which is the better property; just do not read the line above as
      // describing the gap between stimuli on an answered trial.
      gapMs: Math.max(0, isiMs - FIXATION_MS),
    };
  },

  // PRACTICE ONLY — the runner gates this on trial.isPractice. Nothing hidden
  // is used: the ink colour is served and is on screen, and reading it is the
  // whole task. The word is never consulted.
  practiceExpectation: (stimulus) => stimulus.inkColor,

  StimulusView,
};
